import { ClassNamesConfig, components, GroupBase, SelectComponentsConfig } from 'react-select';
import Async, { AsyncProps } from 'react-select/async';

import { DebounceProps, useDebouncedLoadOptions } from '@/hooks/use-debounced-load-options';
import { cn } from '@/lib/utils';

import ArrowDownSmallIcon from '../icons/arrow-down-small';
import CloseSmallIcon from '../icons/close-small';

export function AsyncSelect<Option>({
  styles,
  classNames,
  components,
  loadOptions,
  onInputChange,
  debounceMs = 0,
  ...props
}: AsyncProps<Option, false, GroupBase<Option>> & { error?: boolean } & DebounceProps) {
  const debounced = useDebouncedLoadOptions(loadOptions, onInputChange, debounceMs);
  return (
    <Async
      {...props}
      loadOptions={debounced.loadOptions}
      onInputChange={debounced.onInputChange}
      classNames={{
        ...SELECT_STYLES<Option>(props.error),
        ...classNames,
      }}
      components={{
        ...SELECT_COMPONENTS<Option>(),
        ...components,
      }}
      menuPortalTarget={document.body}
      menuShouldScrollIntoView={false}
      unstyled
      styles={{
        control: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.control?.(base, state) }),
        input: (base, inputProps) => ({ ...base, cursor: 'pointer', ...styles?.input?.(base, inputProps) }),
        option: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.option?.(base, state) }),
        ...styles,
      }}
    />
  );
}

export const SELECT_COMPONENTS: <Option>() => SelectComponentsConfig<Option, false, GroupBase<Option>> = () => ({
  DropdownIndicator: (props) =>
    props.selectProps.isDisabled ? null : (
      <components.DropdownIndicator {...props}>
        <ArrowDownSmallIcon />
      </components.DropdownIndicator>
    ),
  ClearIndicator: (props) => (
    <components.ClearIndicator {...props}>
      <CloseSmallIcon />
    </components.ClearIndicator>
  ),
});

export const SELECT_STYLES: <Option>(error?: boolean) => ClassNamesConfig<Option, false> = (error) => ({
  control: (state) =>
    cn(
      'p-[8px] border-input focus:ring-ring rounded border-2 bg-white px-3 text-sm ring-offset-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 flex items-center',
      state.isFocused && 'border-default-blue',
      error && '!border-default-red',
      state.isDisabled && 'opacity-50',
    ),
  menu: () => 'max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-white text-default-text shadow-md mt-1 !pointer-events-auto',
  menuPortal: () => '!z-50 !pointer-events-auto',
  menuList: () => 'p-1',
  option: (state) =>
    cn(
      'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 !text-sm outline-none',
      state.isFocused && 'bg-grey-1',
      state.isDisabled && 'cursor-default text-secondary-text pointer-events-none',
      state.isSelected && 'bg-grey-1',
    ),
  placeholder: () => 'text-grey-5 leading-none',
  noOptionsMessage: () => 'text-sm text-grey-5 p-2',
  loadingMessage: () => 'text-sm text-grey-5 p-2',
});
